export {Contact as Page};

import {useState, useContext} from "preact/hooks";
import {Helmet} from "react-helmet-async";
import messages from "../../messages/messages.js";
import API_URL from "../../includes/api.js";
import {NotificationContext} from '../../components/NotificationContext.jsx';
import {usePageContext} from '../../hooks/usePageContext.js';
import {RingSpinner} from '../../components/Spinner.jsx';

function Contact() {

    const topic = usePageContext()?.urlParsed?.search?.topic ?? null;
    const {addNotification} = useContext(NotificationContext);

    const [loading, setLoading] = useState(false);
    const [selectedTopic, setSelectedTopic] = useState(topic ?? 'request-song');

    const TOPIC_DESCRIPTIONS = {
        'request-song': 'Interested in getting your music automatically added to our network? <a href="/network">Become a member</a> and enjoy the wide range of benefits we offer.',
        'bug-report': "Found a bug? Please include steps to reproduce the issue, the device and browser you're using and any screenshots if available.",
        'other': "Have another question or feedback? Describe it here and we'll get back to you as soon as possible.",
        'apply-member': "Apply to become a network member — tell us about your channel and why you'd be a good fit.",
        'apply-academy': "Academy application: tell us why you'd benefit from our student program and why you would like to join.",
        'apply-pioneer': "Pioneer application: explain how you'd help grow the community and what initiatives you plan to run.",
        'advertisement': "Do you want to advertise on our website? Please fill out the form below and we will get back to you to discuss possibilities, details and pricing.",
        'business-inquiry': "For business inquiries, include a short description of the proposal so we can route it to the right team.",
    };

    const handleTopicChange = (e) => {
        setSelectedTopic(e.target.value);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true);

        const formData = new FormData();

        // Always append basic fields when present
        ['email', 'discord', 'youtube_url'].forEach(field => {
            const value = e?.target[field]?.value;
            if (value) {
                formData.append(field, value);
            }
        });

        // For application topics we collect multiple inputs and combine them into one labeled message
        const applicationTopics = ['apply-member', 'apply-academy', 'apply-pioneer'];
        if (applicationTopics.includes(selectedTopic)) {
            const portfolio = e?.target['portfolio_links']?.value || '';
            const experience = e?.target['experience']?.value || '';
            const description = e?.target['description']?.value || '';

            // Combine them into a single field with simple labels. This "mixes" the pieces together
            const combined = [
                '--- Application details ---',
                'Portfolio Links:', portfolio,
                'Experience:', experience,
                'Description:', description,
            ].join('\n\n');

            formData.append('message', combined);
        } else {
            // non-application topics: forward the single message field if present
            const msg = e?.target['message']?.value;
            if (msg) formData.append('message', msg);
        }

        formData.append('topic', selectedTopic);
        if (typeof e.target.remember !== 'undefined') {
            formData.append('remember', e.target.remember.checked ? 'Yes' : 'No');
        }

        fetch(API_URL + '/contact/request', {
            method: 'POST',
            body: formData,
        }).then(response => response.json()).then(data => {
            if (data.code === 200) {
                addNotification('Your request has been sent successfully!', 'success');
                e.target.reset();
            } else {
                addNotification(data.message, 'error');
            }
        }).catch((error) => {
            addNotification(error.message, 'error');
        }).finally(() => {
            setLoading(false);
        });
    }


    return (
        <section className="text-white">

            <Helmet>
                <title>{messages["SITE.NAME"]} | Get in touch!</title>
                <meta name="description" content="Get in touch with our team"/>
            </Helmet>

            <div className="flex justify-center">
                <div className="w-full bg-black rounded-lg shadow-sm md:mt-0 sm:max-w-lg xl:p-0">
                    <div className="p-6 space-y-4 md:space-y-6 sm:p-8">
                        <h1 className="text-xl font-bold leading-tight tracking-tight text-white md:text-2xl">
                            Get in touch
                        </h1>
                        <p className="text-sm text-gray-400" style={{marginTop: 0}}>Looking for more information? Please
                            don't hesitate to
                            contact us using the information provided on our contact page. We would love to hear from
                            you and are always happy to help.</p>

                        <form className="space-y-4 md:space-y-6" onSubmit={handleSubmit} method="POST">

                            <div>
                                <label htmlFor="topics"
                                       className="block mb-2 text-sm font-medium text-white">Topic</label>
                                <select
                                    id="topics"
                                    name="topics"
                                    value={selectedTopic}
                                    onChange={handleTopicChange}
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                                >
                                    <optgroup label="General">
                                        <option value="request-song">Request song</option>
                                        <option value="bug-report">Bug Report</option>
                                        <option value="other">Other</option>
                                    </optgroup>

                                    <optgroup label="Applications">
                                        <option value="apply-member">Apply for Network</option>
                                        <option value="apply-academy">Apply for Academy</option>
                                        <option value="apply-pioneer">Apply for Pioneer</option>
                                    </optgroup>

                                    <optgroup label="Business inquiries">
                                        <option value="advertisement">Advertisement</option>
                                        <option value="business-inquiry">Business inquiry</option>
                                    </optgroup>
                                </select>
                            </div>

                            <p className="text-sm text-gray-400" style={{marginTop: 10}} dangerouslySetInnerHTML={{__html: TOPIC_DESCRIPTIONS[selectedTopic] ?? ''}} />

                            <div className="flex flex-wrap -mx-3 mb-6">
                                <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                    <label htmlFor="email" className="block mb-2 text-sm font-medium text-white">Your
                                        email</label>
                                    <input type="email" name="email" id="email"
                                           className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg block w-full p-2.5"
                                           placeholder="name@example.com" required/>
                                </div>

                                <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                    <label htmlFor="discord" className="block mb-2 text-sm font-medium text-white">Your
                                        discord</label>
                                    <input type="text" name="discord" id="discord"
                                           className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg block w-full p-2.5"
                                           placeholder="username" required/>
                                </div>
                            </div>

                            {selectedTopic !== 'advertisement' && selectedTopic !== 'other' && selectedTopic !== 'business-inquiry' && (
                                <div>
                                    <label htmlFor="youtube_url" className="block mb-2 text-sm font-medium text-white">{selectedTopic === 'bug-report' ? 'Bug page URL' : 'YouTube URL'}</label>
                                    <input type="url" name="youtube_url" id="youtube_url"
                                           className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg block w-full p-2.5"
                                           placeholder={selectedTopic === 'bug-report' ? 'https://example.com/page-with-bug' : 'https://youtube.com/'} required/>
                                </div>
                            )}

                            {selectedTopic !== 'request-song' && (
                                ['apply-member', 'apply-academy', 'apply-pioneer'].includes(selectedTopic) ? (
                                    <>
                                        <div>
                                            <label htmlFor="portfolio_links" className="block mb-2 text-sm font-medium text-white">Portfolio links</label>
                                            <input type="text" name="portfolio_links" id="portfolio_links"
                                                   className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg block w-full p-2.5"
                                                   placeholder="https://youtube.com/channel/..., https://soundcloud.com/..." />
                                        </div>

                                        <div>
                                            <label htmlFor="experience" className="block mb-2 text-sm font-medium text-white">Experience</label>
                                            <textarea rows="3" name="experience" id="experience" required
                                                      className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg shadow-xs border border-gray-300"
                                                      placeholder="Years of experience, roles, highlights..." />
                                        </div>

                                        <div>
                                            <label htmlFor="description" className="block mb-2 text-sm font-medium text-white">Why you (short description)</label>
                                            <textarea rows="4" name="description" id="description" required
                                                      className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg shadow-xs border border-gray-300"
                                                      placeholder="Tell us why you'd be a good fit, what you plan to bring to the network..." />
                                        </div>
                                    </>
                                ) : (
                                    <div>
                                        <label htmlFor="message"
                                               className="block mb-2 text-sm font-medium text-white">{selectedTopic === 'bug-report' ? 'Bug details' : 'Message'}</label>
                                        <textarea rows="4" name="message" id="message" required
                                                  className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg shadow-xs border border-gray-300"
                                                  placeholder={selectedTopic === 'bug-report' ? 'Please include steps to reproduce the issue, device & browser, and any screenshots if available.' : 'Leave a message...'}></textarea>

                                    </div>
                                )
                            )}

                            {selectedTopic === 'request-song' && (
                                <div className="flex items-start">
                                    <div className="flex items-center h-5">
                                        <input id="remember" aria-describedby="remember" type="checkbox"
                                               className="w-4 h-4 border rounded-sm border-gray-600 ring-offset-gray-800"
                                               required=""/>
                                    </div>
                                    <div className="ml-3 text-sm">
                                        <label htmlFor="remember" className="text-gray-300">Remind me
                                            when the song has been added.</label>
                                    </div>
                                </div>
                            )}

                            <button type="submit" {...(loading ? {disabled: true} : {})}
                                    className="w-full cursor-pointer text-white border border-white hover:bg-white hover:text-black font-medium rounded-lg text-sm px-5 py-2.5 text-center">Submit {loading && (
                                <RingSpinner/>)}
                            </button>
                            <div>
                                Get help on our <a href={messages.DISCORD_URL} target="_blank" rel="noopener noreferrer">Discord
                                server
                            </a>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    )
        ;
}
